import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { Helmet } from "react-helmet-async";
import { isUserAuthorized } from "./utils/whitelist";
import "./animation.css";

// Import your page components
import LoginPage from "./pages/loginPage";
import UnauthorizedPage from "./pages/unauthorizedPage";
import HomePage from "./pages/homePage";
import ModuleMetricsPage from "./pages/moduleMetricsPage";
import PersonnelMetricsPage from "./pages/personnelMetricsPage";
import ProductionMilestonesPage from "./pages/productionMilestonesPage";
import RtlsMapPage from "./pages/rtlsMapPage";
import Layout from "./pages/components/Layout";

// Protected Route Component with Whitelist Check
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = useIsAuthenticated();
  const { accounts } = useMsal();
  const navigate = useNavigate();
  const [authorizationChecked, setAuthorizationChecked] = useState(false);

  // console.log('=== ProtectedRoute rendered ===');
  // console.log('isAuthenticated:', isAuthenticated);
  // console.log('accounts:', accounts);

  useEffect(() => {
    // console.log('=== ProtectedRoute useEffect triggered ===');
    // console.log('isAuthenticated:', isAuthenticated);
    // console.log('accounts.length:', accounts.length);

    if (isAuthenticated && accounts.length > 0) {
      const userEmail = accounts[0].username;
      // console.log('Checking authorization for user:', userEmail);

      const authorized = isUserAuthorized(userEmail);
      console.log("isUserAuthorized result:", authorized);

      if (!authorized) {
        // console.log('User NOT authorized - redirecting to /unauthorized');
        navigate("/unauthorized", { replace: true });
      } else {
        // console.log('User IS authorized - allowing access');
        setAuthorizationChecked(true);
      }
    } else {
      // console.log('User not authenticated or no accounts found');
    }
  }, [isAuthenticated, accounts, navigate]);

  if (!isAuthenticated) {
    // console.log('ProtectedRoute: User not authenticated - redirecting to /login');
    return <Navigate to="/login" replace />;
  }

  if (isAuthenticated && !authorizationChecked) {
    // console.log('ProtectedRoute: Showing authorization check loading screen');
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: "#f7fafc",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: "50px",
              height: "50px",
              margin: "0 auto 20px",
              border: "4px solid #e0e0e0",
              borderTopColor: "#cc0000",
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
            }}
          ></div>
          <p style={{ color: "#2d2d2d", fontSize: "16px" }}>
            Verifying access...
          </p>
        </div>
      </div>
    );
  }

  // console.log('ProtectedRoute: Rendering children');
  return children;
};

function App() {
  const isAuthenticated = useIsAuthenticated();
  const { accounts } = useMsal();

  // console.log('=== App component rendered ===');
  // console.log('isAuthenticated:', isAuthenticated);
  // console.log('accounts:', accounts);

  // Check if authenticated user is authorized
  const isAuthorized =
    isAuthenticated && accounts.length > 0
      ? isUserAuthorized(accounts[0].username)
      : false;

  // console.log('isAuthorized:', isAuthorized);

  if (isAuthenticated && accounts.length > 0) {
    // console.log('Current user:', accounts[0].username);
    // console.log('User authorization status:', isAuthorized);
  }

  return (
    <>
      <Helmet>
        <title>RTLS Dashboard - PROD</title>
        <meta
          name="description"
          content="Real-Time Location System Dashboard"
        />
      </Helmet>

      <Routes>
        {/* Public Route - Login */}
        <Route
          path="/login"
          element={(() => {
            // console.log('=== /login route evaluated ===');
            // console.log('isAuthenticated:', isAuthenticated);
            // console.log('isAuthorized:', isAuthorized);

            if (isAuthenticated && isAuthorized) {
              // console.log('User authenticated and authorized - redirecting to /home');
              return <Navigate to="/home" replace />;
            } else if (isAuthenticated && !isAuthorized) {
              // console.log('User authenticated but NOT authorized - redirecting to /unauthorized');
              return <Navigate to="/unauthorized" replace />;
            } else {
              // console.log('User not authenticated - showing login page');
              return <LoginPage />;
            }
          })()}
        />

        {/* Unauthorized Page */}
        <Route path="/unauthorized" element={<UnauthorizedPage />} />

        {/* Protected Routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/home" replace />} />
          <Route path="home" element={<HomePage />} />
          <Route path="module-metrics" element={<ModuleMetricsPage />} />
          <Route path="personnel-metrics" element={<PersonnelMetricsPage />} />
          <Route
            path="production-milestones"
            element={<ProductionMilestonesPage />}
          />
          <Route path="rtls-map" element={<RtlsMapPage />} />
        </Route>

        {/* Catch all - redirect based on auth and authorization */}
        <Route
          path="*"
          element={(() => {
            // console.log('=== Catch-all route evaluated ===');
            const redirectPath =
              isAuthenticated && isAuthorized
                ? "/home"
                : isAuthenticated
                  ? "/unauthorized"
                  : "/login";
            // console.log('Redirecting to:', redirectPath);
            return <Navigate to={redirectPath} replace />;
          })()}
        />
      </Routes>
    </>
  );
}

export default App;
